/** @jsxImportSource @emotion/react */
import {
  Button,
  createStyles,
  Snackbar,
  useForm,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useMount, useAsyncFn } from 'react-use';
import { submitAnswers } from './api';
import { Steps } from './components/Steps';
import { Success } from './steps';
import {
  ContactInformation,
  KybFormData,
  KybSave,
  KycUseFormType,
  Merchant,
  MerchantUseFormType,
} from './types';
import { kybValidators, merchantValidators } from './utils';
import appendFormDataFromUrl from './utils/appendFormDataFromUrl';

export type MerchantContext = {
  merchantUseForm: MerchantUseFormType;
  kybUseForm: KycUseFormType;
  email: string;
  loading: boolean;
  onSubmit: () => void;
  setEmail: (value: string) => void;
  onUpdateQueryParam: (key: string, value: string | number) => void;
};

const defaultMerchantForm: Merchant = {
  merchantId: undefined,
  countryCode: '',
  name: '',
  tosAccepted: false,
};

const defaultContactInfo: ContactInformation = {
  address1: '',
  country: '',
  postalNumber: '',
  postalRegion: '',
};

const defaultKybForm: KybFormData = {
  fullCompanyName: '',
  registrationNumber: '',
  incorporationCountry: '',
  businessSectors: [],
  expectedMonthlyVolume: 0,
  businessOperations: '',
  reasonForUse: '',
  jurisdictions: [],
  turnover: 0,
  expectedMonthlyTransactions: 0,
  registeredAddress: defaultContactInfo,
  relatedPeople: [],
};

function onUpdateQueryParam(key: string, value: string | number) {
  const params = new URLSearchParams(window.location.search);
  if (value) {
    params.set(key, String(value));
  } else {
    params.delete(key);
  }
  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.replaceState({}, '', newUrl);
}

function getMerchantFormDataFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return {
    email: params.get('email') ?? '',
    kyb: {
      fullCompanyName: params.get('fullCompanyName') ?? '',
      registrationNumber: params.get('registrationNumber') ?? '',
      businessSectors: JSON.parse(
        decodeURIComponent(params.get('businessSectors') ?? '[]')
      ),
      incorporationCountry: params.get('incorporationCountry') ?? '',
      businessOperations: params.get('businessOperations') ?? '',
      reasonForUse: params.get('reasonForUse') ?? '',
      jurisdictions: JSON.parse(
        decodeURIComponent(params.get('jurisdictions') ?? '[]')
      ),
      expectedMonthlyVolume: Number(params.get('expectedMonthlyVolume') ?? 0),
      turnover: Number(params.get('turnover') ?? 0),
      expectedMonthlyTransactions: Number(
        params.get('expectedMonthlyTransactions') ?? 0
      ),
      registeredAddress: {
        address1: params.get('address1') ?? '',
        postalNumber: params.get('postalNumber') ?? '',
        country: params.get('country') ?? '',
        postalRegion: params.get('postalRegion') ?? '',
      },
    },
    merchant: {
      merchantId: params.get('merchantId') ?? '',
      countryCode: params.get('countryCode') ?? '',
      name: params.get('name') ?? '',
    },
  };
}

const base_steps = [
  { path: 'general-information', label: 'Lets get you signed up' },
  { path: 'business-information', label: 'Business Information' },
];

export interface StepProps {
  basePath: string;
}

export const SignUp = ({ basePath }: StepProps) => {
  const navigate = useNavigate();
  const styles = useStyles();
  const location = useLocation();
  const [email, setEmail] = useState<string>();
  const merchantUseForm = useForm<Merchant>(
    defaultMerchantForm,
    merchantValidators
  );
  const steps = base_steps.map((step) => ({ ...step, basePath }));
  const kybUseForm = useForm<KybFormData>(defaultKybForm, kybValidators);
  const currentStepIndex = steps.findIndex((step) =>
    location.pathname.includes(step.path)
  );

  const onShareForm = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  useMount(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.size > 0) {
      const formDataFromUrl = getMerchantFormDataFromUrl();
      if (formDataFromUrl.email) {
        setEmail(formDataFromUrl.email);
      }
      appendFormDataFromUrl<Merchant>(
        formDataFromUrl.merchant,
        merchantUseForm
      );
      appendFormDataFromUrl<KybFormData>(formDataFromUrl.kyb, kybUseForm);
      merchantUseForm.setters.name.set(formDataFromUrl.kyb.fullCompanyName);
    }
  });

  useEffect(() => {
    if (currentStepIndex === -1) {
      navigate(steps[0].path, { replace: true });
      return;
    }
  }, [steps, currentStepIndex, navigate]);

  const [{ value: formResponse, loading, error }, onSubmit] =
    useAsyncFn(async () => {
      if (!email || merchantUseForm.hasErrors || kybUseForm.hasErrors)
        return null;

      const filteredJurisdictions = {
        ...kybUseForm.fields,
        jurisdictions: kybUseForm.fields.jurisdictions
          .map(({ value }) => value)
          .join(', '),
      };
      const response = await submitAnswers(
        email,
        merchantUseForm.fields,
        filteredJurisdictions as KybSave
      );
      return response;
    }, [email, merchantUseForm, kybUseForm]);

  const [showError, setShowError] = useState(false);
  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  return (
    <div css={styles.container}>
      <Snackbar
        message={[
          'Looks like something went wrong...',
          `${error?.reason}`,
          'Please contact support.',
        ]}
        variant="danger"
        open={showError}
        onClose={() => setShowError(false)}
      />
      {formResponse?.merchantId ? (
        <Success
          merchantName={kybUseForm.fields.fullCompanyName}
          email={email}
        />
      ) : (
        <>
          <div css={styles.stepContainer}>
            <Steps steps={steps} currentStepIndex={currentStepIndex} />
            <div>
              <Button variant="icon" icon="copy" onClick={onShareForm} />
            </div>
          </div>
          <Outlet
            context={{
              loading,
              merchantUseForm,
              kybUseForm,
              email,
              setEmail,
              onSubmit,
              onUpdateQueryParam,
            }}
          />
        </>
      )}
    </div>
  );
};

const useStyles = createStyles(({ spacing, palette, breakpoints, sizing }) => ({
  container: {
    padding: `0 ${spacing.m}`,
    marginTop: spacing.xxs,
    [breakpoints.laptopSmall]: {
      width: `calc(1.5*${sizing.large})`,
    },
  },
  stepContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing.m,
    [breakpoints.laptopSmall]: {
      marginBottom: spacing.s,
    },
  },
  step: {
    color: '#888',
    marginRight: spacing.xxxs,
    borderBottom: `4px solid ${palette.grey.x100}`,
  },
}));
